import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  X, 
  Heart, 
  Reply, 
  Clock, 
  User,
  Send,
  MoreVertical
} from 'lucide-react';
import { 
  saveLectureDiscussion, 
  getLectureDiscussions,
  updateLectureDiscussion,
  deleteLectureDiscussion 
} from '../firebase/collections';
import { listenToLectureDiscussions } from '../firebase/realtime';
import { formatTimestamp } from '../utils/timestampUtils';

const DiscussionPanel = ({ 
  lectureId, 
  currentTime, 
  currentUser, 
  onClose,
  formatTime 
}) => {
  const [discussions, setDiscussions] = useState([]);
  const [newDiscussion, setNewDiscussion] = useState({ content: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Load discussions on component mount
  useEffect(() => {
    if (!lectureId) return;

    const unsubscribe = listenToLectureDiscussions(lectureId, (result) => {
      if (result.success) {
        setDiscussions(result.data);
        setLoading(false);
      } else {
        setError(result.error);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [lectureId]);

  const handleSubmitDiscussion = async (e) => {
    e.preventDefault();
    
    if (!newDiscussion.content.trim()) {
      return;
    }

    if (!currentUser?.uid) {
      return;
    }

    // Check for inappropriate content
    const moderationCheck = checkForInappropriateContent(newDiscussion.content.trim());
    if (moderationCheck.hasInappropriateContent) {
      showModerationWarning(moderationCheck.foundWords);
      return;
    }

    setIsSubmitting(true);
    try {
      const discussionData = {
        content: newDiscussion.content.trim(),
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'Anonymous',
        authorAvatar: currentUser.photoURL || 'https://via.placeholder.com/40',
        timestamp: currentTime,
        likes: 0,
        replies: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const result = await saveLectureDiscussion(lectureId, discussionData);
      
      if (result.success) {
        setNewDiscussion({ content: '' });
        // Real-time listener will automatically update the discussions
      } else {
      }
    } catch (error) {
      // Error creating discussion
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (discussionId, currentLikes) => {
    try {
      await updateLectureDiscussion(discussionId, {
        likes: currentLikes + 1
      });
    } catch (error) {
      // Error liking discussion
    }
  };

  const handleReply = async (discussionId) => {
    if (!replyContent.trim()) {
      return;
    }

    if (!currentUser?.uid) {
      return;
    }

    // Check for inappropriate content
    const moderationCheck = checkForInappropriateContent(replyContent.trim());
    if (moderationCheck.hasInappropriateContent) {
      showModerationWarning(moderationCheck.foundWords);
      return;
    }

    setSubmittingReply(true);
    try {
      const replyData = {
        content: replyContent.trim(),
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'Anonymous',
        authorAvatar: currentUser.photoURL || 'https://via.placeholder.com/40',
        timestamp: currentTime,
        createdAt: new Date()
      };

      // Get current discussion to update replies
      const discussion = discussions.find(d => d.id === discussionId);
      const updatedReplies = [...(discussion.replies || []), replyData];

      await updateLectureDiscussion(discussionId, {
        replies: updatedReplies
      });

      setReplyContent('');
      setReplyingTo(null);
    } catch (error) {
      // Error posting reply
    } finally {
      setSubmittingReply(false);
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyContent('');
  };

  const formatDiscussionTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    try {
      // Use the formatTimestamp utility which handles Firestore timestamps properly
      const formattedDate = formatTimestamp(timestamp, { includeTime: false, short: true });
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    } catch (error) {
      // Error formatting discussion time
      return 'Just now';
    }
  };

  const formatVideoTime = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDisplayTime = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return 'Start';
    if (minutes < 5) return 'Early';
    if (minutes < 10) return 'Beginning';
    if (minutes < 20) return 'First quarter';
    if (minutes < 30) return 'First half';
    if (minutes < 45) return 'Middle';
    if (minutes < 60) return 'Second half';
    return 'Later part';
  };

  const addTimestampToContent = () => {
    const timestamp = formatVideoTime(currentTime);
    const timestampText = `[${timestamp}] `;
    setNewDiscussion(prev => ({
      ...prev,
      content: prev.content + timestampText
    }));
  };

  // Content moderation function
  const checkForInappropriateContent = (content) => {
    const inappropriateWords = [
      // Hate speech and slurs
      'hate', 'racist', 'sexist', 'homophobic', 'transphobic', 'bigot',
      // Abusive language
      'stupid', 'idiot', 'moron', 'dumb', 'retard', 'fool',
      // Profanity (common variations)
      'fuck', 'shit', 'bitch', 'ass', 'damn', 'hell',
      // Threats
      'kill', 'hurt', 'attack', 'fight', 'beat', 'punch',
      // Discriminatory terms
      'nazi', 'fascist', 'terrorist', 'extremist'
    ];

    const contentLower = content.toLowerCase();
    const foundWords = inappropriateWords.filter(word => 
      contentLower.includes(word)
    );

    return {
      hasInappropriateContent: foundWords.length > 0,
      foundWords: foundWords
    };
  };

  const showModerationWarning = (foundWords) => {
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Lecture Discussion</h3>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          title="Close"
        >
          <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
        </button>
      </div>

      {/* Discussion Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          {/* New Discussion Form */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Start a Discussion</h4>
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
              <strong>Community Guidelines:</strong> Please keep discussions respectful, constructive, and free from hate speech, abusive language, or inappropriate content. This is a learning environment for everyone.
            </div>
            <form 
              onSubmit={handleSubmitDiscussion} 
              className="space-y-3"
              onKeyDown={(e) => {
                // Prevent keyboard events from bubbling up to video player
                if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
                  e.stopPropagation();
                }
              }}
            >
              <textarea
                placeholder="Share your thoughts, ask questions, or discuss this lecture..."
                rows={4}
                value={newDiscussion.content}
                onChange={(e) => setNewDiscussion(prev => ({ ...prev, content: e.target.value }))}
                onKeyDown={(e) => {
                  // Prevent space key from bubbling up to video player
                  if (e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isSubmitting}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">
                    Current: {formatDisplayTime(currentTime)}
                  </span>
                  <button
                    type="button"
                    onClick={addTimestampToContent}
                    className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors flex items-center space-x-1"
                    title="Add precise timestamp to your message"
                  >
                    <Clock className="w-3 h-3" />
                    <span>Add Time</span>
                  </button>
                </div>
                <button 
                  type="submit"
                  disabled={isSubmitting || !newDiscussion.content.trim()}
                  className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Posting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Post Discussion</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Discussion List */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900">Recent Discussions</h4>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-gray-500 text-sm">Loading discussions...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-500 text-sm">Error loading discussions</p>
                <p className="text-gray-400 text-xs mt-1">{error.message}</p>
              </div>
            ) : discussions.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No discussions yet</p>
                <p className="text-gray-400 text-xs mt-1">Be the first to start a discussion!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {discussions.map((discussion) => (
                  <motion.div 
                    key={discussion.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                  >
                    <div className="flex items-start space-x-3 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-medium text-gray-900">
                            {discussion.authorName}
                          </h5>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatDisplayTime(discussion.timestamp)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDiscussionTime(discussion.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-700 mb-3">
                      {discussion.content}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button 
                          onClick={() => handleLike(discussion.id, discussion.likes || 0)}
                          className="flex items-center space-x-1 text-xs text-gray-500 hover:text-red-500 transition-colors"
                        >
                          <Heart className="w-3 h-3" />
                          <span>{discussion.likes || 0}</span>
                        </button>
                        <button 
                          onClick={() => setReplyingTo(replyingTo === discussion.id ? null : discussion.id)}
                          className="flex items-center space-x-1 text-xs text-gray-500 hover:text-blue-500 transition-colors"
                        >
                          <Reply className="w-3 h-3" />
                          <span>{discussion.replies?.length || 0} replies</span>
                        </button>
                      </div>
                      
                      {currentUser?.uid === discussion.authorId && (
                        <button className="text-xs text-gray-400 hover:text-gray-600">
                          <MoreVertical className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Reply Form */}
                    {replyingTo === discussion.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex-1">
                          <textarea
                            placeholder="Write your reply..."
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                            rows={2}
                            disabled={submittingReply}
                          />
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">
                                Current: {formatDisplayTime(currentTime)}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  const timestamp = formatVideoTime(currentTime);
                                  const timestampText = `[${timestamp}] `;
                                  setReplyContent(prev => prev + timestampText);
                                }}
                                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors flex items-center space-x-1"
                                title="Add precise timestamp to your reply"
                              >
                                <Clock className="w-3 h-3" />
                                <span>Add Time</span>
                              </button>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={cancelReply}
                                className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                disabled={submittingReply}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleReply(discussion.id)}
                                disabled={submittingReply || !replyContent.trim()}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {submittingReply ? 'Posting...' : 'Reply'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Replies List */}
                    {discussion.replies && discussion.replies.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="space-y-3">
                          {discussion.replies.map((reply, index) => (
                            <div key={index} className="flex items-start space-x-2 pl-4">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-xs font-medium text-gray-900">
                                    {reply.authorName}
                                  </span>
                                  <span className="text-xs text-gray-500 flex items-center">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {formatDisplayTime(reply.timestamp)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDiscussionTime(reply.createdAt)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700">
                                  {reply.content}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscussionPanel;
